import { useSelection } from '../util/react-util';
import { Item } from '../components/product_event_table/product_event_table';
import { useEffect, useState } from 'react';
import {
  GetMerchProductCategoriesForVoucherCampaignQuery,
  GetProductsAndEventsByVoucherCampaignIdQuery,
  GetShopEventsAndMerchProductsQuery,
} from '../generated/graphql';

export function useProductEventTableSelection(
  data: GetProductsAndEventsByVoucherCampaignIdQuery['products'] | GetShopEventsAndMerchProductsQuery['products'],
  allowedCategories?: GetMerchProductCategoriesForVoucherCampaignQuery['categories']['nodes']
): {
  items: string[];
  mappedItems: Item[];
  size: number;
  handleSelectAllDisplayed: () => void;
  handleSelectAllItems: () => void;
  handleRowSelect: (variantIds: Array<string> | string) => void;
  clear: () => void;
  selectionType: 'single' | 'multiple' | 'all';
  allSelected: boolean;
} {
  const [mappedItems, setMappedItems] = useState<Array<Item>>([]);
  const [variantIds, setVariantIds] = useState<string[]>([]);
  const { allSelected, values, size, toggle, toggleAll, clear, selectAll, selectionType } = useSelection<string>();

  function handleSelectAllDisplayed(): void {
    toggleAll(variantIds);
  }

  function handleSelectAllItems(): void {
    selectAll(variantIds);
  }

  function handleRowSelect(selectedRowVariantIds: Array<string> | string): void {
    if (typeof selectedRowVariantIds === 'string') {
      toggle(selectedRowVariantIds);
      return;
    }

    const similarCheckValue = selectedRowVariantIds.every((id) => values.includes(id));
    if (similarCheckValue) {
      toggle(selectedRowVariantIds);
    } else {
      toggle(selectedRowVariantIds.filter((id) => !values.includes(id)));
    }
  }

  useEffect(() => {
    if (data) {
      const mapped = mapProducts(data, allowedCategories);
      setMappedItems(mapped);

      const allVariantIds = getAllProductVariantIds(mapped);
      setVariantIds(allVariantIds);
    }
  }, [data, allowedCategories]);

  useEffect(() => {
    if (allSelected) handleSelectAllItems();
  }, [mappedItems]);

  return {
    items: values,
    size,
    handleRowSelect,
    handleSelectAllDisplayed,
    handleSelectAllItems,
    clear,
    selectionType,
    allSelected,
    mappedItems,
  };
}

function mapProducts(
  data?: GetProductsAndEventsByVoucherCampaignIdQuery['products'] | GetShopEventsAndMerchProductsQuery['products'],
  allowedCategoriesArray?: GetMerchProductCategoriesForVoucherCampaignQuery['categories']['nodes']
): Array<Item> {
  let allowedCategories: Set<string> | null;
  if (allowedCategoriesArray) {
    allowedCategories = new Set(allowedCategoriesArray.map((category) => category.id));
  }

  return data
    ? data.edges.map((edge: (typeof data)['edges'][0]) => {
        const { node } = edge;
        const productVariants: Item['productVariants'] = [];
        let disabled = false;

        if ('productVariantsForVoucherCampaign' in node) {
          node.productVariantsForVoucherCampaign.forEach((variant) => {
            productVariants.push({
              id: variant.id,
              sku: variant.sku,
              description: variant.description || '-',
              estimatedAvailability: variant.estimatedAvailability,
              netPrice: variant.netPrice || 0,
              price: variant.price || 0,
              pricingCategory: variant.pricingCategory?.name || '-',
              associatedWithVoucherCampaign: false,
            });
            disabled = false;
          });
        } else if ('productVariants' in node) {
          let associatedCount = 0;

          node.productVariants.nodes.forEach((variant) => {
            productVariants.push({
              id: variant.id,
              description: variant.description || '-',
              estimatedAvailability: variant.estimatedAvailability,
              associatedWithVoucherCampaign: variant.associatedWithVoucherCampaign,
              pricingCategory: variant.pricingCategory?.name || '-',
              sku: variant.sku,
              netPrice: variant.netPrice || 0,
              price: variant.price || 0,
            });

            if (variant.associatedWithVoucherCampaign) {
              associatedCount++;
            }
          });

          if (node.productVariants.nodes.length === associatedCount) {
            disabled = true;
          }
        }

        let item: Item = {
          id: node.id,
          sku: node.sku,
          artist: node.artist,
          name: node.name,
          productVariants,
          disabled,
          price: 0,
          categories: [],
          // Filled later
          estimatedAvailability: 0,
          productType: 'unknown',
          color: 'unknown',
          venue: 'unknown',
          date: 'unknown',
        };

        if (node.__typename === 'Event') {
          item = {
            ...item,
            venue: node.venue.name,
            date: node.happeningFrom,
          };
        } else if (node.__typename === 'MerchProduct') {
          item = {
            ...item,
            productType: node.productType,
            color: node.color,
            price: node.price,
            estimatedAvailability: node.estimatedAvailability,
            categories: node.categories.nodes
              .filter((category) => {
                if (!allowedCategories) return true;

                return allowedCategories.has(category.id);
              })
              .map((node) => node.name),
          };
        }
        return item;
      })
    : [];
}

function getAllProductVariantIds(data: Array<Item>): Array<string> {
  return data.flatMap((item) =>
    item.productVariants.filter((node) => !node.associatedWithVoucherCampaign).map((node) => node.id)
  );
}
